import Decimal from 'decimal.js';
import { compact, first, isEmpty } from 'lodash';

import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { ACCESS_PARAMETER_FREQUENCY_DISPLAY_NAMES } from '@/modules/beneficiaries/beneficiaries.constants';
import { TrustBeneficiariesList_AccessParameterFragment } from '@/modules/trusts/TrustBeneficiariesList/graphql/TrustBeneficiariesList.fragments.generated';
import {
  AccessParameterFrequency,
  EntityGstStatus,
  EntityInEstateStatus,
  Maybe,
  NonTrustEntityTaxStatus,
} from '@/types/schema';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { formatPercent } from '@/utils/formatting/percent';
import { getNodes } from '@/utils/graphqlUtils';

import {
  UNITRUST_TYPE_ITEMS_V2,
  validUnitrustTypesV2,
} from '../EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { EntityType } from '../types/EntityType';
import { getEntityTypeFromSubtype } from '../utils/getEntityTypeFromSubtype';
import { EntityDetail_EntityFragment } from './graphql/EntityDetailPage.generated';

export interface EntityCardProps {
  entity: EntityDetail_EntityFragment;
  entityType: EntityType;
  onUpdate: () => void;
  hasValuation?: boolean;
  isPanelView: boolean;
}

export const EntityGstStatusDisplayNames: Record<EntityGstStatus, string> = {
  [EntityGstStatus.GstExempt]: 'GST exempt',
  [EntityGstStatus.GstNonExempt]: 'GST non-exempt',
  [EntityGstStatus.MixedGst]: 'Mixed GST',
};

export const EntityInEstateStatusDisplayNames: Record<
  EntityInEstateStatus,
  string
> = {
  [EntityInEstateStatus.InEstate]: 'In estate',
  [EntityInEstateStatus.OutOfEstate]: 'Out of estate',
};

export function getSharedTopLevelProperties(data: EntityDetail_EntityFragment) {
  if (!('id' in data.subtype)) {
    throw new Error(
      `Invalid trust subtype ${
        (data.subtype as EntityDetail_EntityFragment['subtype']).__typename
      }`
    );
  }

  return {
    entityType: getEntityTypeFromSubtype(data.subtype.__typename!),
    subtypeId: data.subtype.id,
  };
}

export function getPossibleLifetimeDurationLabelItem({
  termDurationLifetime,
  termDurationYears,
}: {
  termDurationLifetime: boolean;
  termDurationYears: number | null;
}) {
  if (termDurationLifetime) {
    return {
      label: 'Term length',
      value: 'Lifetime',
    };
  }

  if (termDurationYears) {
    return {
      label: 'Term length',
      value: `${termDurationYears} years`,
    };
  }

  return {
    label: 'Term length',
    value: EMPTY_CONTENT_HYPHEN,
  };
}

const nonTrustEntityTaxStatusDisplayNames: Record<
  NonTrustEntityTaxStatus,
  string
> = {
  [NonTrustEntityTaxStatus.NonTaxable]: 'Non-taxable entity',
  [NonTrustEntityTaxStatus.Taxable]: 'Taxable entity',
};

export function getFormattedNonTrustEntityTaxStatus(
  taxStatus: Maybe<NonTrustEntityTaxStatus>
): {
  value: string | undefined;
} {
  return {
    value: taxStatus
      ? nonTrustEntityTaxStatusDisplayNames[taxStatus]
      : undefined,
  };
}

export function getPayoutInformation({
  entity,
}: {
  entity: EntityCardProps['entity'];
}) {
  if (
    entity.subtype.__typename !== 'CRTTrust' &&
    entity.subtype.__typename !== 'CLTTrust'
  ) {
    throw new Error('Invalid trust type');
  }

  const lifetimeBeneficiaryAccessParameters =
    entity.subtype.lifetimeBeneficiaries?.reduce<
      TrustBeneficiariesList_AccessParameterFragment[]
    >((acc, beneficiary) => {
      const accessParameters: TrustBeneficiariesList_AccessParameterFragment[] =
        compact(getNodes(beneficiary.accessParameters));

      return acc.concat(accessParameters);
    }, []) ?? [];

  // we pull the first one the isUnitrust flag; while the others are ignored here,
  // when writing, they _should_ all use the same distribution kind
  const firstLifetimeBeneficiaryAccessParameter = first(
    lifetimeBeneficiaryAccessParameters
  );

  // If we do not have access params, we return the default
  if (
    !firstLifetimeBeneficiaryAccessParameter ||
    isEmpty(lifetimeBeneficiaryAccessParameters)
  ) {
    return {
      payoutType: EMPTY_CONTENT_HYPHEN,
      payoutAmount: EMPTY_CONTENT_HYPHEN,
    };
  }

  const unitrustTypes = validUnitrustTypesV2;
  const distributionKind = firstLifetimeBeneficiaryAccessParameter.kind;

  let displayFrequency = EMPTY_CONTENT_HYPHEN;

  if (firstLifetimeBeneficiaryAccessParameter.frequency) {
    displayFrequency =
      ACCESS_PARAMETER_FREQUENCY_DISPLAY_NAMES[
        AccessParameterFrequency.Annually
      ].toLowerCase();
  }

  // Either "Annuity" or "Unitrust (x)"
  const payoutType = (() => {
    const isUnitrust = unitrustTypes.includes(distributionKind);

    const unitrustLabel = UNITRUST_TYPE_ITEMS_V2.find(
      (item) => item.value === distributionKind
    )?.summaryLabel;

    if (isUnitrust && !unitrustLabel) {
      throw new Error('Invalid unitrust type');
    }

    if (isUnitrust && unitrustLabel) {
      return `Unitrust (${unitrustLabel})`;
    }

    return 'Annuity';
  })();

  const isUnitrust = unitrustTypes.includes(distributionKind);
  const payoutDecimalAmount =
    lifetimeBeneficiaryAccessParameters.reduce<Decimal>(
      (acc, { amount, percentage }) => {
        const distribution = isUnitrust ? percentage : amount;
        return acc.plus(distribution ?? 0);
      },
      new Decimal(0)
    );

  let payoutAmount: string = EMPTY_CONTENT_HYPHEN;

  if (!payoutDecimalAmount.isZero()) {
    if (isUnitrust) {
      payoutAmount = `${formatPercent(payoutDecimalAmount, 1)}% ${displayFrequency}`;
    } else {
      payoutAmount = `${formatCurrencyNoDecimals(payoutDecimalAmount)} ${displayFrequency}`;
    }
  }

  return {
    payoutType,
    payoutAmount,
  };
}
